import { useCallback, useMemo, useState } from 'react';
import {
  Brush,
  ClipboardPaste,
  Copy,
  Droplets,
  Eraser,
  FlipHorizontal,
  FlipVertical,
  Move,
  PaintBucket,
  PaintRoller,
  Palette,
  Plus,
  RotateCcw,
  Save,
  Bookmark,
  SquareDashed,
  Trash2,
  Waves,
} from 'lucide-react';
import { useEditor } from '../../state/store';
import {
  DEFAULT_ANIMATION,
  DEFAULT_GRADIENT,
  Easing,
  GradientKind,
  GradientTarget,
  offsetCycle,
} from '../../tools/gradient';
import {
  GRADIENT_PRESETS,
  SavedGradient,
  listSavedGradients,
  removeGradient,
  saveGradient,
} from '../../state/library';
import { colorToHex, hexToColor } from '../../core/types';
import { Checkbox, Field, Hint, NumberInput, Panel, Segmented } from '../widgets/common';
import { Select } from '../widgets/primitives';

/** Actions that operate on the current selection. */
export function SelectionPanel(): JSX.Element {
  const revision = useEditor((s) => s.revision);
  const brush = useEditor((s) => s.brush);
  const store = useEditor.getState();
  const tab = store.active();
  const [componentName, setComponentName] = useState('');

  const selected = tab.selection.size;
  const bounds = tab.selection.getBounds();

  return (
    <>
      <Panel title="Selection" collapsible={false}>
        <div className="row" key={revision} style={{ gap: 8 }}>
          <SquareDashed size={15} className={selected ? 'ok-text' : 'faint'} />
          <span className="mono dim">
            {bounds ? `${bounds.x},${bounds.y}  ${bounds.width}×${bounds.height}` : 'nothing selected'}
          </span>
        </div>
        <Hint>
          {selected
            ? 'Drag inside the selection to move it — it stops at the document edge instead of falling off. Painting tools stay inside it too.'
            : 'With nothing selected these actions apply to the whole layer.'}
        </Hint>
      </Panel>

      <Panel title="Apply to selection" collapsible={false}>
        <button
          className="option"
          onClick={() => store.fillSelection()}
          title={`Fill every selected cell with "${brush.char}"`}
        >
          <PaintBucket size={16} />
          <span className="label">Fill with character</span>
          <span className="meta">{brush.char}</span>
        </button>
        <button
          className="option"
          onClick={() => store.applyGradientToSelection()}
          title="Run the gradient across the selection"
        >
          <Droplets size={16} />
          <span className="label">Apply gradient</span>
        </button>
        <button
          className="option"
          onClick={() => store.fillSelectionColor('bg')}
          title="Paint the background of every selected cell, leaving the characters alone"
        >
          <PaintRoller size={16} />
          <span className="label">Fill background</span>
          <span
            style={{
              width: 13,
              height: 13,
              borderRadius: 3,
              background: colorToHex(brush.bg),
              border: '1px solid var(--border-strong)',
            }}
          />
        </button>
        <button
          className="option"
          onClick={() => store.fillSelectionColor('fg')}
          title="Recolour the characters without changing which characters they are"
        >
          <Brush size={16} />
          <span className="label">Fill text colour</span>
          <span
            style={{
              width: 13,
              height: 13,
              borderRadius: 3,
              background: colorToHex(brush.fg),
              border: '1px solid var(--border-strong)',
            }}
          />
        </button>
        <button className="option" onClick={() => store.clearSelectionColors()}>
          <Palette size={16} />
          <span className="label">Clear colours</span>
        </button>
        <button className="option" onClick={() => store.deleteSelection()}>
          <Eraser size={16} />
          <span className="label">Erase</span>
          <span className="meta">Del</span>
        </button>
      </Panel>

      <Panel title="Transform">
        <div className="grid2">
          <button className="btn solid" onClick={() => store.flipSelection('h')}>
            <FlipHorizontal size={14} /> Flip H
          </button>
          <button className="btn solid" onClick={() => store.flipSelection('v')}>
            <FlipVertical size={14} /> Flip V
          </button>
          <button className="btn solid" onClick={() => store.copySelection()}>
            <Copy size={14} /> Copy
          </button>
          <button className="btn solid" onClick={() => store.paste()}>
            <ClipboardPaste size={14} /> Paste
          </button>
        </div>
        <Hint>
          <Move size={11} style={{ verticalAlign: -1 }} /> Arrow keys nudge the selection one cell.
        </Hint>
      </Panel>

      <Panel title="Select by">
        <div className="grid2">
          <button className="btn solid" onClick={() => store.selectAll()}>
            All
          </button>
          <button className="btn solid" onClick={() => store.invertSelection()}>
            <RotateCcw size={13} /> Invert
          </button>
          <button className="btn solid" onClick={() => store.selectNone()}>
            None
          </button>
          <button
            className="btn solid"
            onClick={() => store.selectByChar(brush.char)}
            title={`Every cell containing "${brush.char}"`}
          >
            Char {brush.char}
          </button>
          <button className="btn solid" onClick={() => store.selectByColor(brush.fg, 'fg')}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: colorToHex(brush.fg),
                border: '1px solid var(--border-strong)',
              }}
            />
            Foreground
          </button>
          <button className="btn solid" onClick={() => store.selectByColor(brush.bg, 'bg')}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: colorToHex(brush.bg),
                border: '1px solid var(--border-strong)',
              }}
            />
            Background
          </button>
        </div>
      </Panel>

      <Panel title="Save as component">
        <div className="row">
          <input
            className="input"
            placeholder="Component name"
            value={componentName}
            onChange={(e) => setComponentName(e.target.value)}
          />
          <button
            className="btn solid"
            disabled={!componentName.trim() || !bounds}
            onClick={() => {
              store.saveComponent(componentName.trim());
              setComponentName('');
            }}
          >
            <Save size={14} />
          </button>
        </div>
      </Panel>
    </>
  );
}

const KINDS: ReadonlyArray<{ value: GradientKind; label: string }> = [
  { value: 'horizontal', label: '→' },
  { value: 'vertical', label: '↓' },
  { value: 'diagonal', label: '↘' },
  { value: 'radial', label: '◎' },
  { value: 'conic', label: '◔' },
];

/** Multi-stop gradient editor, shared by the gradient tool and the apply action. */
export function GradientPanel(): JSX.Element {
  const brush = useEditor((s) => s.brush);
  const store = useEditor.getState();
  const gradient = brush.gradient;
  const update = (patch: Partial<typeof gradient>) =>
    store.setBrush({ gradient: { ...gradient, ...patch } });

  const sorted = [...gradient.stops].sort((a, b) => a.position - b.position);

  return (
    <Panel title="Gradient" collapsible={false}>
      <div
        style={{
          height: 22,
          borderRadius: 'var(--r-sm)',
          border: '1px solid var(--border-strong)',
          marginBottom: 9,
          background: `linear-gradient(90deg, ${sorted
            .map((s) => `${colorToHex(s.color)} ${Math.round(s.position * 100)}%`)
            .join(', ')})`,
        }}
      />

      <Field label="Shape">
        <Segmented
          value={gradient.kind}
          options={KINDS.map((k) => ({ value: k.value, label: k.label, title: k.value }))}
          onChange={(v) => update({ kind: v })}
        />
      </Field>
      <Field label="Applies to">
        <Segmented
          value={gradient.target}
          options={[
            { value: 'fg' as GradientTarget, label: 'Text' },
            { value: 'bg' as GradientTarget, label: 'Background' },
            { value: 'both' as GradientTarget, label: 'Both' },
          ]}
          onChange={(v) => update({ target: v })}
        />
      </Field>

      {gradient.stops.map((stop, i) => (
        <div className="row" key={i} style={{ marginBottom: 5 }}>
          <input
            type="color"
            value={colorToHex(stop.color)}
            onChange={(e) => {
              const stops = [...gradient.stops];
              stops[i] = { ...stop, color: hexToColor(e.target.value) };
              update({ stops });
            }}
          />
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(stop.position * 100)}
            style={{ flex: 1 }}
            onChange={(e) => {
              const stops = [...gradient.stops];
              stops[i] = { ...stop, position: Number(e.target.value) / 100 };
              update({ stops });
            }}
          />
          <button
            className="icon-btn"
            title="Remove stop"
            disabled={gradient.stops.length <= 2}
            onClick={() => update({ stops: gradient.stops.filter((_, j) => j !== i) })}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}

      <div className="grid2" style={{ marginTop: 5 }}>
        <button
          className="btn solid"
          onClick={() => update({ stops: [...gradient.stops, { position: 1, color: brush.fg }] })}
        >
          <Plus size={14} /> Add stop
        </button>
        <button
          className="btn solid"
          title="Back to the default green–cyan–blue ramp"
          onClick={() => update({ stops: [...DEFAULT_GRADIENT.stops] })}
        >
          <RotateCcw size={13} /> Reset
        </button>
      </div>
      <Checkbox
        label="Mirror ramp"
        checked={gradient.mirror ?? false}
        onChange={(v) => update({ mirror: v })}
        title="Run the ramp out and back so both ends match"
      />

      <GradientLibrary
        onApply={(def) => store.setBrush({ gradient: { ...def, stops: def.stops.map((s) => ({ ...s })) } })}
        current={gradient}
      />
      <button
        className="btn primary tall"
        style={{ width: '100%', marginTop: 7 }}
        onClick={() => store.applyGradientToSelection()}
      >
        <Droplets size={15} /> Apply to selection
      </button>
      <Hint>Or pick the Gradient tool and drag on the canvas to set the ramp direction.</Hint>

      <PulsePanel />
    </Panel>
  );
}

/** Swatch of a ramp, used by the preset and saved lists. */
function RampSwatch({ stops }: { stops: ReadonlyArray<{ position: number; color: number }> }): JSX.Element {
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  return (
    <span
      style={{
        flex: 1,
        height: 16,
        borderRadius: 3,
        border: '1px solid var(--border-strong)',
        background: `linear-gradient(90deg, ${sorted
          .map((s) => `${colorToHex(s.color)} ${Math.round(s.position * 100)}%`)
          .join(', ')})`,
      }}
    />
  );
}

/**
 * Ready-made ramps and the user's own.
 *
 * A gradient is fiddly to rebuild from memory — several stops, positions and a
 * shape — so the ones worth keeping are kept.
 */
function GradientLibrary({
  onApply,
  current,
}: {
  onApply: (def: (typeof GRADIENT_PRESETS)[number]['gradient']) => void;
  current: (typeof GRADIENT_PRESETS)[number]['gradient'];
}): JSX.Element {
  const [saved, setSaved] = useState<SavedGradient[]>(() => listSavedGradients());
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const refresh = useCallback(() => setSaved(listSavedGradients()), []);

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!saveGradient(trimmed, current)) {
      useEditor.getState().notify('Could not save — browser storage is unavailable.', 'error');
      return;
    }
    setName('');
    setNaming(false);
    refresh();
  };

  return (
    <>
      <div className="group-title" style={{ marginTop: 12 }}>
        My gradients
        <span className="spacer" />
        <button className="icon-btn" title="Save the current ramp" onClick={() => setNaming(true)}>
          <Bookmark size={13} />
        </button>
      </div>

      {naming ? (
        <div className="row" style={{ marginBottom: 8 }}>
          <input
            className="input"
            autoFocus
            placeholder="Gradient name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') setNaming(false);
              e.stopPropagation();
            }}
          />
          <button className="btn solid" onClick={save} disabled={!name.trim()}>
            Save
          </button>
        </div>
      ) : null}

      {saved.length === 0 && !naming ? <Hint>Save a ramp you want to reuse.</Hint> : null}

      {saved.map((entry) => (
        <div className="row" key={entry.id} style={{ marginBottom: 4 }}>
          <button
            className="btn"
            style={{ flex: 1, gap: 8, padding: '0 6px' }}
            onClick={() => onApply(entry.gradient)}
            title={`Apply ${entry.name}`}
          >
            <RampSwatch stops={entry.gradient.stops} />
            <span style={{ width: 72, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {entry.name}
            </span>
          </button>
          <button
            className="icon-btn"
            title="Delete"
            onClick={() => {
              removeGradient(entry.id);
              refresh();
            }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}

      <div className="group-title" style={{ marginTop: 12 }}>
        Presets
      </div>
      {GRADIENT_PRESETS.map((preset) => (
        <button
          key={preset.name}
          className="btn"
          style={{ width: '100%', gap: 8, padding: '0 6px', marginBottom: 4 }}
          onClick={() => onApply(preset.gradient)}
          title={`Apply ${preset.name}`}
        >
          <RampSwatch stops={preset.gradient.stops} />
          <span style={{ width: 92, textAlign: 'left' }}>{preset.name}</span>
        </button>
      ))}
    </>
  );
}

/**
 * Pulse: a gradient whose colours travel across the selection.
 *
 * The ramp is baked into animation frames rather than animated live, because a
 * frame here is a set of visible layers — which is also what makes the result
 * play in the timeline, the exported ANSI, the runtime player and the SSH
 * server, instead of being something only the editor can show.
 */
function PulsePanel(): JSX.Element {
  const store = useEditor.getState();
  const gradient = useEditor((s) => s.brush.gradient);

  const [frames, setFrames] = useState(12);
  const [fps, setFps] = useState(12);
  const [mode, setMode] = useState<'scroll' | 'pulse'>('scroll');
  const [direction, setDirection] = useState<'forward' | 'reverse'>('forward');
  const [easing, setEasing] = useState<Easing>('linear');
  const [pingPong, setPingPong] = useState(false);

  // The same offsets the animation will use, so the preview is the thing
  // itself rather than an impression of it.
  const preview = useMemo(
    () => offsetCycle({ ...DEFAULT_ANIMATION, mode, direction, easing, pingPong, fps }, frames),
    [mode, direction, easing, pingPong, fps, frames],
  );

  const sorted = [...gradient.stops].sort((a, b) => a.position - b.position);
  const ramp = (offset: number) =>
    `linear-gradient(90deg, ${sorted
      .map((s) => `${colorToHex(s.color)} ${Math.round(((s.position + offset) % 1) * 100)}%`)
      .join(', ')})`;

  return (
    <>
      <div className="group-title" style={{ marginTop: 12 }}>
        Pulse
      </div>
      <Hint>
        Sends the colours travelling across the selection. Each step becomes an animation frame, so
        it plays in the timeline and exports like any other animation.
      </Hint>

      {/* One swatch per frame: the cycle at a glance, and whether it loops. */}
      <div className="row" style={{ gap: 2, margin: '8px 0' }}>
        {preview.map((offset, i) => (
          <span
            key={i}
            title={`Frame ${i + 1} — offset ${offset.toFixed(2)}`}
            style={{
              flex: 1,
              height: 18,
              borderRadius: 2,
              border: '1px solid var(--border)',
              background: ramp(offset),
            }}
          />
        ))}
      </div>

      <Field label="Motion">
        <Segmented
          value={mode}
          options={[
            { value: 'scroll' as const, label: 'Travel', title: 'Colours run across and wrap around' },
            { value: 'pulse' as const, label: 'Breathe', title: 'The ramp swells and fades in place' },
          ]}
          onChange={setMode}
        />
      </Field>
      <Field label="Direction">
        <Segmented
          value={direction}
          options={[
            { value: 'forward' as const, label: '→' },
            { value: 'reverse' as const, label: '←' },
          ]}
          onChange={setDirection}
        />
      </Field>
      <Field label="Easing">
        <Select
          ariaLabel="Easing"
          value={easing}
          options={[
            { value: 'linear' as Easing, label: 'Linear', hint: 'steady' },
            { value: 'sine' as Easing, label: 'Sine', hint: 'soft at both ends' },
            { value: 'easeIn' as Easing, label: 'Ease in', hint: 'starts slow' },
            { value: 'easeOut' as Easing, label: 'Ease out', hint: 'ends slow' },
            { value: 'easeInOut' as Easing, label: 'Ease in-out' },
          ]}
          onChange={setEasing}
        />
      </Field>

      <div className="field-pair">
        <Field label="Frames">
          <NumberInput value={frames} min={2} max={60} onChange={setFrames} />
        </Field>
        <Field label="FPS">
          <NumberInput value={fps} min={1} max={60} onChange={setFps} />
        </Field>
      </div>

      <Checkbox
        label="Ping-pong"
        checked={pingPong}
        onChange={setPingPong}
        title="Run the colours out and back instead of wrapping"
      />

      <Hint>
        {frames} frames at {fps} fps — {(frames / Math.max(1, fps)).toFixed(1)}s per loop.
      </Hint>

      <button
        className="btn primary tall"
        style={{ width: '100%', marginTop: 7 }}
        onClick={() => store.createGradientPulse({ mode, direction, easing, pingPong, fps }, frames)}
      >
        <Waves size={15} /> Create pulse animation
      </button>
    </>
  );
}
